import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leaveBalanceApi } from '../api/leaveBalanceApi';
import { appToast } from '../../../../components/AppToast';

export function useLeaveBalanceList() {
  const navigate = useNavigate();
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('All');
  const [yearFilter, setYearFilter] = useState('2026');
  const [searchText, setSearchText] = useState('');
  const [searchField, setSearchField] = useState('Leave Type');
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({
    totalOpening: '36.00',
    totalConsume: '3.00',
    totalLaps: '0.00',
    totalCurrent: '33.00',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const leaveTypeOptions = [
    { value: 'All', label: 'All Leave Types' },
    { value: 'Casual Leave', label: 'Casual Leave' },
    { value: 'Sick Leave', label: 'Sick Leave' },
    { value: 'Privilege Leave', label: 'Privilege Leave' },
    { value: 'Compensatory Off', label: 'Compensatory Off' },
    { value: 'Maternity / Paternity Leave', label: 'Maternity / Paternity Leave' },
  ];

  const yearOptions = [
    { value: '2026', label: 'Year 2026 (Current)' },
    { value: '2025', label: 'Year 2025' },
    { value: '2024', label: 'Year 2024' },
  ];

  const searchFieldOptions = [
    { value: 'Leave Type', label: 'Leave Type' },
    { value: 'Department', label: 'Department' },
  ];

  const tools = [
    { key: 'excel', label: 'Export to Excel', icon: 'table_view' },
    { key: 'pdf', label: 'Export to PDF', icon: 'picture_as_pdf' },
    { key: 'print', label: 'Print Statement', icon: 'print' },
  ];

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await leaveBalanceApi.getSummary();
      const items = res.data || [];
      setRecords(items);
      const totalOp = items.reduce((acc, it) => acc + Number(it.opening || it.OpBalance || 0), 0);
      const totalCons = items.reduce((acc, it) => acc + Number(it.consume || it.Consume || 0), 0);
      const totalCur = items.reduce((acc, it) => acc + Number(it.current || it.CurrentBalance || 0), 0);
      setSummary({
        totalOpening: totalOp.toFixed(2),
        totalConsume: totalCons.toFixed(2),
        totalLaps: '0.00',
        totalCurrent: totalCur.toFixed(2),
      });
    } catch {
      // fallback
    } finally {
      setIsLoading(false);
    }
  }, []);


  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtered records
  const filteredRecords = useMemo(() => {
    return records.filter((item) => {
      // Leave Type filter
      if (leaveTypeFilter !== 'All' && item.leaveType !== leaveTypeFilter) {
        return false;
      }

      // Year filter
      if (yearFilter && item.year !== yearFilter) {
        return false;
      }

      // Text search
      if (searchText.trim()) {
        const query = searchText.trim().toLowerCase();
        if (searchField === 'Leave Type' && !item.leaveType.toLowerCase().includes(query)) return false;
        if (searchField === 'Department' && !item.department.toLowerCase().includes(query)) return false;
      }

      return true;
    });
  }, [records, leaveTypeFilter, yearFilter, searchText, searchField]);

  // Pagination
  const totalCount = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  // Actions
  const handleApplyLeave = useCallback(
    (leaveType) => {
      navigate(`/leave-apply/entry?leaveType=${encodeURIComponent(leaveType || '')}`);
    },
    [navigate]
  );

  const handleSearch = useCallback(() => {
    setPage(1);
    appToast.success('Filters applied');
  }, []);

  const resetFilters = useCallback(() => {
    setLeaveTypeFilter('All');
    setYearFilter('2026');
    setSearchText('');
    setPage(1);
  }, []);

  const handleToolSelect = useCallback((toolKey) => {
    const key = String(toolKey || '').toLowerCase();
    if (key.includes('print')) {
      window.print();
    } else if (key.includes('excel')) {
      appToast.success('Exporting Leave Balances to Excel...');
    } else if (key.includes('pdf')) {
      appToast.success('Exporting Leave Balances to PDF...');
    }
  }, []);

  return {
    state: {
      records: paginatedRecords,
      totalCount,
      page,
      pageSize,
      totalPages,
      leaveTypeFilter,
      leaveTypeOptions,
      yearFilter,
      yearOptions,
      searchText,
      searchField,
      isLoading,
      summary,
      searchFieldOptions,
      tools,
    },
    actions: {
      setLeaveTypeFilter,
      setYearFilter,
      setSearchText,
      setSearchField,
      setPage,
      setPageSize,
      handleApplyLeave,
      handleSearch,
      resetFilters,
      handleToolSelect,
      refresh: loadData,
    },
  };
}
